import { format, formatDistance, formatRelative, isValid } from 'date-fns';
import { parseCalendarDate } from './calendarDate';

/**
 * Utility functions for formatting data consistently across the application
 */

/**
 * Format a number as USD currency
 *
 * @param value - Numeric value to format
 * @param options - Intl.NumberFormat options
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1234.56) // "$1,235"
 * formatCurrency(1234.56, { minimumFractionDigits: 2 }) // "$1,234.56"
 */
export const formatCurrency = (
  value: number,
  options?: Intl.NumberFormatOptions
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(value);
};

/**
 * Format an instant (createdAt, updatedAt, uploadedAt) as a readable local date. Not for date-only
 * values such as contract or cost record dates: use `formatCalendarDate` for those (#65).
 *
 * @param date - Date to format
 * @param formatString - date-fns format string (default: 'MMM dd, yyyy')
 * @returns Formatted date string or empty string if invalid
 *
 * @example
 * formatDate(new Date('2024-01-15')) // "Jan 15, 2024"
 * formatDate(new Date('2024-01-15'), 'yyyy-MM-dd') // "2024-01-15"
 */
export const formatDate = (
  date: Date | string | null | undefined,
  formatString: string = 'MMM dd, yyyy'
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!isValid(dateObj)) {
    console.warn('Invalid date provided to formatDate:', date);
    return '';
  }

  return format(dateObj, formatString);
};

/**
 * Format a calendar date without any timezone conversion
 *
 * Every date-only value (contract start, end, renewal and notice deadline, cost record dates,
 * license expiration, milestones; #14, #65) is a calendar day stored as UTC midnight in a
 * `timestamptz` column. Use this, never `formatDate`, for them; see `utils/calendarDate.ts`.
 * `formatDate` would parse "2026-12-01T00:00:00Z" into a local Date, and a browser in a
 * western timezone (America/Chicago) would then show Nov 30, 2026. This helper takes the
 * `YYYY-MM-DD` part of the ISO string and builds a local Date from those parts, so the
 * day shown is the day stored wherever the viewer is.
 *
 * @param value - ISO date string (the first ten characters must be YYYY-MM-DD)
 * @param formatString - date-fns format string (default: 'MMM dd, yyyy')
 * @returns Formatted date string, or '' for empty or unparsable input
 *
 * @example
 * formatCalendarDate('2026-12-01T00:00:00Z') // "Dec 01, 2026"
 * formatCalendarDate('2026-12-01T00:00:00Z', 'yyyy-MM-dd') // "2026-12-01"
 */
export const formatCalendarDate = (
  value: string | null | undefined,
  formatString: string = 'MMM dd, yyyy'
): string => {
  const dateObj = parseCalendarDate(value);
  return dateObj ? format(dateObj, formatString) : '';
};

/**
 * Format a date as a relative time string (e.g., "2 days ago")
 *
 * @param date - Date to format
 * @param baseDate - Base date to compare against (default: now)
 * @returns Relative time string or empty string if invalid
 *
 * @example
 * formatRelativeTime(new Date('2024-01-13')) // "2 days ago"
 */
export const formatRelativeTime = (
  date: Date | string | null | undefined,
  baseDate: Date = new Date()
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!isValid(dateObj)) {
    console.warn('Invalid date provided to formatRelativeTime:', date);
    return '';
  }

  return formatDistance(dateObj, baseDate, { addSuffix: true });
};

/**
 * Format a date relative to now (e.g., "today at 3:00 PM")
 *
 * @param date - Date to format
 * @param baseDate - Base date to compare against (default: now)
 * @returns Relative date string or empty string if invalid
 *
 * @example
 * formatRelativeDate(new Date()) // "today at 3:00 PM"
 */
export const formatRelativeDate = (
  date: Date | string | null | undefined,
  baseDate: Date = new Date()
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!isValid(dateObj)) {
    console.warn('Invalid date provided to formatRelativeDate:', date);
    return '';
  }

  return formatRelative(dateObj, baseDate);
};

/**
 * Format a number with thousand separators
 *
 * @param value - Number to format
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format a percentage value
 *
 * @param value - Decimal value (0-1) to format as percentage
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercentage(0.8567) // "86%"
 * formatPercentage(0.8567, 2) // "85.67%"
 */
export const formatPercentage = (value: number | null | undefined, decimals: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Truncate a string to a maximum length with ellipsis
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string with ellipsis if needed
 *
 * @example
 * truncateText("This is a long string", 10) // "This is a..."
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format file size in human-readable format
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1572864) // "1.5 MB"
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format a phone number (basic US format)
 *
 * @param phone - Phone number string
 * @returns Formatted phone number
 *
 * @example
 * formatPhoneNumber("1234567890") // "(123) 456-7890"
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  const match = cleaned.match(/^(\d{1,3})(\d{0,3})(\d{0,4})$/);

  if (match) {
    const part1 = match[1];
    const part2 = match[2];
    const part3 = match[3];

    if (part3) {
      return `(${part1}) ${part2}-${part3}`;
    } else if (part2) {
      return `(${part1}) ${part2}`;
    } else {
      return `(${part1}`;
    }
  }

  return phone;
};

/**
 * Format an amount in a stored currency code without ever throwing (#18). The validators accept
 * any three-character code, and Intl.NumberFormat throws a RangeError on one it does not know
 * (`US$`), so an unknown code falls back to the grouped number followed by the code.
 *
 * @example
 * formatMoney(1500, 'EUR') // "€1,500"
 * formatMoney(1500, 'US$') // "1,500 US$"
 */
export const formatMoney = (value: number, currency: string | null | undefined): string => {
  const code = currency?.trim() || 'USD';
  try {
    return formatCurrency(value, { currency: code });
  } catch {
    return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)} ${code}`;
  }
};
